// API Response Types
export interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  count: number;
  total: number;
  limit: number;
  offset: number;
}

// Authentication Types
export interface AuthConfig {
  apiToken: string;
  baseUrl?: string;
  timeout?: number;
  maxRetries?: number;
}

// Session Types
export interface Session {
  _id?: string;
  tel: string;
  name: string;
  state: SessionState;
  ready: boolean;
  initializing?: boolean;
  qr?: string;
  environment?: string;
  autoRestart?: boolean;
  createdAt?: string;
  updatedAt?: string;
}

export enum SessionState {
  DISCONNECTED = 'DISCONNECTED',
  INITIALIZING = 'INITIALIZING',
  CONNECTED = 'CONNECTED',
  CONNECTING = 'CONNECTING'
}

export interface CreateSessionRequest {
  tel: string;
  name: string;
  autoRestart?: boolean;
  clientUrl?: string;
  onMessageHook?: string;
}

export interface UpdateSessionRequest {
  name?: string;
  autoRestart?: boolean;
  clientUrl?: string;
  onMessageHook?: string;
}

// Message Types
export interface Message {
  _id?: string;
  id: MessageId;
  type: MessageType;
  body?: string;
  timestamp: number;
  from: string;
  to: string;
  media?: MessageMedia;
  status?: number;
  hasReaction?: boolean;
  reactions?: MessageReaction[];
}

export interface MessageId {
  remoteJid: string;
  fromMe: boolean;
  id: string;
}

export enum MessageType {
  CHAT = 'chat',
  IMAGE = 'image',
  VIDEO = 'video',
  AUDIO = 'audio',
  DOCUMENT = 'document',
  STICKER = 'sticker',
  LOCATION = 'location',
  CONTACT_CARD = 'contact_card',
  CONTACT_CARD_MULTI = 'contact_card_multi',
  REVOKED = 'revoked',
  ORDER = 'order',
  PRODUCT = 'product',
  PAYMENT = 'payment',
  BUTTONS = 'buttons',
  LIST = 'list',
  BUTTONS_RESPONSE = 'buttons_response',
  LIST_RESPONSE = 'list_response',
  POLL = 'poll'
}

export interface MessageMedia {
  url?: string;
  mimetype?: string;
  filename?: string;
  filesize?: number;
  height?: number;
  width?: number;
  caption?: string;
}

export interface MessageReaction {
  key: MessageId;
  text: string;
  timestamp: number;
}

export interface SendMessageRequest {
  tel: string;
  to: string;
  text?: string;
  media?: string; // base64 encoded
  mimetype?: string;
  filename?: string;
  caption?: string;
  options?: MessageOptions;
}

export interface MessageOptions {
  quotedMessageId?: string;
  mentions?: string[];
  sendSeen?: boolean;
  linkPreview?: boolean;
}

export interface ListMessagesParams {
  tel?: string;
  chatId?: string;
  fromDate?: string;
  toDate?: string;
  hasMedia?: boolean;
  limit?: number;
  offset?: number;
}

// Contact Types
export interface Contact {
  id: string;
  name?: string;
  notify?: string;
  imgUrl?: string;
  status?: string;
  isBusiness?: boolean;
  isEnterprise?: boolean;
  isMyContact?: boolean;
  isUser?: boolean;
}

export interface ContactsResponse {
  success: boolean;
  count: number;
  contacts: Contact[];
}

// Group Types
export interface Group {
  id: string;
  name: string;
  desc?: string;
  owner?: string;
  subject: string;
  creation?: number;
  participants: GroupParticipant[];
  admins: string[];
  isCommunity?: boolean;
  isLocked?: boolean;
  isAnnounce?: boolean;
}

export interface GroupParticipant {
  id: string;
  admin?: 'admin' | 'superadmin' | null;
}

export interface CreateGroupRequest {
  tel: string;
  name: string;
  participants: string[];
}

export interface UpdateGroupRequest {
  subject?: string;
  desc?: string;
  locked?: boolean;
  announce?: boolean;
}

// Status/Story Types
export interface Status {
  id: string;
  from: string;
  timestamp: number;
  type: 'text' | 'image' | 'video';
  content?: string;
  media?: string;
  caption?: string;
  backgroundColor?: string;
  font?: number;
  isForwarded?: boolean;
  duration?: number;
  views?: string[];
  expiration?: number;
}

export interface StatusUpdate {
  tel: string;
  type: 'text' | 'image' | 'video';
  content?: string;
  media?: string;
  caption?: string;
  backgroundColor?: string;
  font?: number;
  duration?: number;
}

// Webhook Types
export interface Webhook {
  _id: string;
  sessionId: string;
  tel: string;
  name: string;
  url: string;
  events: string[];
  headers?: Record<string, string>;
  active: boolean;
  retryConfig: {
    maxAttempts: number;
    backoffMultiplier: number;
    initialDelay: number;
  };
  statistics: {
    totalCalls: number;
    successfulCalls: number;
    failedCalls: number;
    lastCallAt?: string;
    lastSuccessAt?: string;
    lastFailureAt?: string;
    lastError?: string;
    averageResponseTime?: number;
  };
  createdAt: string;
  updatedAt: string;
}

export interface CreateWebhookRequest {
  tel: string;
  name: string;
  url: string;
  events: string[];
  headers?: Record<string, string>;
  retryConfig?: {
    maxAttempts: number;
    backoffMultiplier: number;
    initialDelay: number;
  };
}

export interface UpdateWebhookRequest {
  tel: string;
  webhookId: string;
  name?: string;
  url?: string;
  events?: string[];
  headers?: Record<string, string>;
  active?: boolean;
  retryConfig?: {
    maxAttempts: number;
    backoffMultiplier: number;
    initialDelay: number;
  };
}

export interface WebhookTestResult {
  success: boolean;
  status?: number;
  statusText?: string;
  responseTime?: number;
  headers?: Record<string, any>;
  error?: string;
  details?: any;
}

export interface WebhookConfig {
  id?: string;
  url: string;
  events: WebhookEvent[];
  active?: boolean;
  headers?: Record<string, string>;
  secret?: string;
}

export enum WebhookEvent {
  MESSAGE_RECEIVED = 'message.received',
  MESSAGE_SENT = 'message.sent',
  MESSAGE_DELIVERED = 'message.delivered',
  MESSAGE_READ = 'message.read',
  MESSAGE_DELETED = 'message.deleted',
  SESSION_CONNECTED = 'session.connected',
  SESSION_DISCONNECTED = 'session.disconnected',
  SESSION_QR = 'session.qr',
  CONTACT_UPDATED = 'contact.updated',
  GROUP_UPDATED = 'group.updated',
  GROUP_PARTICIPANTS_UPDATED = 'group.participants.updated',
  PRESENCE_UPDATED = 'presence.updated'
}

// Business Types
export interface BusinessProfile {
  id: string;
  name?: string;
  category?: string;
  description?: string;
  email?: string;
  website?: string[];
  address?: string;
  hours?: BusinessHours[];
}

export interface BusinessHours {
  day: string;
  open: string;
  close: string;
}

export interface Product {
  id: string;
  name: string;
  description?: string;
  price: string;
  currency: string;
  isHidden?: boolean;
  url?: string;
  retailerId?: string;
  imageUrl?: string;
}

export interface Catalog {
  products: Product[];
  paging?: {
    cursors: {
      before: string;
      after: string;
    };
  };
}

// Interactive Message Types
export interface InteractiveMessage {
  type: 'button' | 'list';
  header?: {
    type: 'text' | 'image' | 'video' | 'document';
    text?: string;
    media?: string;
  };
  body: {
    text: string;
  };
  footer?: {
    text: string;
  };
  action: ButtonAction | ListAction;
}

export interface ButtonAction {
  buttons: Button[];
}

export interface Button {
  type: 'reply' | 'url' | 'call';
  reply?: {
    id: string;
    title: string;
  };
  url?: {
    displayText: string;
    url: string;
  };
  call?: {
    displayText: string;
    phoneNumber: string;
  };
}

export interface ListAction {
  button: string;
  sections: ListSection[];
}

export interface ListSection {
  title?: string;
  rows: ListRow[];
}

export interface ListRow {
  id: string;
  title: string;
  description?: string;
}

// Poll Types
export interface Poll {
  name: string;
  options: string[];
  selectableCount?: number;
}

export interface PollVote {
  pollMessageId: string;
  optionNames: string[];
}

// Presence Types
export interface PresenceUpdate {
  tel: string;
  chatId: string;
  presence: 'available' | 'composing' | 'recording' | 'paused';
}

// Privacy Types
export interface PrivacySettings {
  lastSeen?: 'all' | 'contacts' | 'contact_blacklist' | 'none';
  online?: 'all' | 'match_last_seen';
  profilePhoto?: 'all' | 'contacts' | 'contact_blacklist' | 'none';
  status?: 'all' | 'contacts' | 'contact_blacklist' | 'none';
  readReceipts?: 'all' | 'none';
  groupAdd?: 'all' | 'contacts' | 'contact_blacklist';
  disappearingDuration?: number;
}

// Error Types
export interface ApiError extends Error {
  status?: number;
  code?: string;
  details?: any;
}

// Client Options
export interface ClientOptions extends AuthConfig {
  retryConfig?: {
    retries?: number;
    retryDelay?: number;
    retryCondition?: (error: any) => boolean;
  };
  headers?: Record<string, string>;
}